import { ChangeDetectionStrategy, Component, Input, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ServiceCompanyMudCheckPropertiesDto } from '../../models/service-company-dto.interface';
import { JobRequestReportDto } from '../../models/job-request-report-dto.interface';
import { CellDefinition, Worksheet } from '../../shared/components/spreadsheet/spreadsheet.models';
import {
  calculateNafChemistryTitration,
  calculateNafCorrectedSolidsVolumePercent,
  calculateNafSolidsComposition,
  calculateNafWaterPhaseSalinity
} from '../../shared/utils/naf-chemistry-calculations';

interface RheologyReportRow {
  temperature?: number;
  rpm600?: number;
  rpm300?: number;
  rpm200?: number;
  rpm100?: number;
  rpm6?: number;
  rpm3?: number;
  plasticViscosity?: number;
  yieldPoint?: number;
  gel10Sec?: number;
  gel10Min?: number;
  gel30Min?: number;
}

interface ParticleSizeRow {
  percentile: number;
  sizeLessThan?: number;
  diameter: number;
  cumulativeLess?: number;
}

interface NafChemistryReportValues {
  alkalinity?: number;
  chlorides?: number;
  limeContent?: number;
  calcium?: number;
  waterPhaseSalinity?: number;
  correctedSolids?: number;
  lowGravitySolids?: number;
}

interface ParticleChartPoint {
  x: number;
  y: number;
  size: number;
  percentile: number;
  incremental: number;
  barY: number;
  barHeight: number;
  label: string;
}

interface CustomReportParameter {
  key: string;
  name: string;
  unit?: string | null;
  value: string | null;
  formula?: string | null;
}

interface CustomReportTableColumn {
  name: string;
  key?: string | null;
  kind: 'input' | 'output';
  unit?: string | null;
  formula?: string | null;
}

interface CustomReportTable {
  name: string;
  key?: string | null;
  rowCount: number;
  columns: CustomReportTableColumn[];
}

interface CustomReportSpreadsheetCell {
  address: string;
  row: number;
  column: number;
  definition: CellDefinition | null;
  value: string | null;
}

interface CustomReport {
  label: string;
  testName: string;
  inputParameters: CustomReportParameter[];
  outputParameters: CustomReportParameter[];
  table: CustomReportTable | null;
  spreadsheet: Worksheet | null;
  spreadsheetCells: CustomReportSpreadsheetCell[];
  values: Record<string, string | null>;
}

interface CustomReportDefinition {
  testName?: string;
  inputParameters?: Array<{ name: string; key?: string | null; unit?: string | null }>;
  outputParameters?: Array<{ name: string; key?: string | null; unit?: string | null; formula?: string | null }>;
  table?: CustomReportTable | null;
  spreadsheet?: Worksheet | null;
}

const PARTICLE_SIZE_BINS = [
  0.58, 0.69, 0.81, 0.96, 1.14, 1.35, 1.61, 1.90, 2.25, 2.67,
  3.16, 3.75, 4.45, 5.28, 6.25, 7.41, 8.78, 10.4, 12.3, 14.6,
  17.3, 20.5, 24.3, 28.8, 34.1, 40.4, 47.9, 56.7, 67.1, 79.5,
  94.2, 112, 132, 157, 186, 220, 261, 309, 366, 434,
  514, 609, 722, 855, 1010, 1200, 1420, 1690, 2000
];

@Component({
  selector: 'app-mud-check-report',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './mud-check-report.component.html',
  styleUrl: './mud-check-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MudCheckReportComponent {
  protected readonly mudCheckData = signal<ServiceCompanyMudCheckPropertiesDto | null>(null);
  protected readonly reportData = signal<JobRequestReportDto | null>(null);
  protected readonly customTestReports = computed<CustomReport[]>(() =>
    this.createCustomTestReports(this.reportData())
  );
  protected readonly resolvedMudCheckData = computed(() =>
    this.mudCheckData() ?? this.createMudCheckDataFromOverview(this.reportData())
  );
  protected readonly nafChemistryValues = computed<NafChemistryReportValues>(() =>
    this.calculateNafChemistryReportValues(this.reportData(), this.resolvedMudCheckData())
  );
  protected readonly rheologyRows = computed<RheologyReportRow[]>(() => {
    const overviewValues = this.createOverviewValueMap(this.reportData());
    const temperatures = [...overviewValues.keys()]
      .map(key => /^t(\d+)_rpm600$/.exec(key)?.[1])
      .filter((value, index, all): value is string => !!value && all.indexOf(value) === index)
      .sort((left, right) => Number(left) - Number(right));

    if (temperatures.length) {
      return temperatures.map(temperature => ({
        temperature: Number(temperature),
        rpm600: this.numberValue(overviewValues, `t${temperature}_rpm600`),
        rpm300: this.numberValue(overviewValues, `t${temperature}_rpm300`),
        rpm200: this.numberValue(overviewValues, `t${temperature}_rpm200`),
        rpm100: this.numberValue(overviewValues, `t${temperature}_rpm100`),
        rpm6: this.numberValue(overviewValues, `t${temperature}_rpm6`),
        rpm3: this.numberValue(overviewValues, `t${temperature}_rpm3`),
        gel10Sec: this.numberValue(overviewValues, `t${temperature}_gel10s`),
        gel10Min: this.numberValue(overviewValues, `t${temperature}_gel10m`),
        gel30Min: this.numberValue(overviewValues, `t${temperature}_gel30m`)
      }));
    }

    const data = this.resolvedMudCheckData();
    return data ? [{
      temperature: data.rheologyTemperature,
      rpm600: data.rpm600,
      rpm300: data.rpm300,
      rpm200: data.rpm200,
      rpm100: data.rpm100,
      rpm6: data.rpm6,
      rpm3: data.rpm3,
      plasticViscosity: data.plasticViscosity,
      yieldPoint: data.yieldPoint,
      gel10Sec: data.gel10Sec,
      gel10Min: data.gel10Min
    }] : [];
  });
  protected readonly particleSizeRows = computed<ParticleSizeRow[]>(() => {
    const values = this.createOverviewValueMap(this.reportData());
    return [
      { percentile: 10, sizeLessThan: this.numberValue(values, 'd10', 'percentile10_size'), diameter: 6, cumulativeLess: this.numberValue(values, 'size6_percentile') },
      { percentile: 50, sizeLessThan: this.numberValue(values, 'd50', 'percentile50_size'), diameter: 44, cumulativeLess: this.numberValue(values, 'size44_percentile') },
      { percentile: 90, sizeLessThan: this.numberValue(values, 'd90', 'percentile90_size'), diameter: 74, cumulativeLess: this.numberValue(values, 'size74_percentile') }
    ];
  });
  protected readonly particleChartPoints = computed<ParticleChartPoint[]>(() => {
    const values = this.createOverviewValueMap(this.reportData());
    const measurements = [5, 10, 25, 40, 50, 60, 75, 85, 90, 95]
      .map(percentile => ({ percentile, size: this.numberValue(values, `percentile${percentile}_size`) }))
      .filter((item): item is { percentile: number; size: number } => item.size !== undefined)
      .sort((left, right) => left.size - right.size);

    if (!measurements.length) return [];
    const anchors = [
      { size: PARTICLE_SIZE_BINS[0], percentile: 0 },
      ...measurements,
      { size: PARTICLE_SIZE_BINS[PARTICLE_SIZE_BINS.length - 1], percentile: 100 }
    ];
    let previousPercentile = 0;

    return PARTICLE_SIZE_BINS.map((size, index) => {
      const upperIndex = anchors.findIndex(anchor => anchor.size >= size);
      const upper = anchors[Math.max(upperIndex, 1)];
      const lower = anchors[Math.max(upperIndex - 1, 0)];
      const logRange = Math.log(upper.size) - Math.log(lower.size);
      const position = logRange > 0 ? (Math.log(size) - Math.log(lower.size)) / logRange : 0;
      const percentile = lower.percentile + position * (upper.percentile - lower.percentile);
      const incremental = Math.max(percentile - previousPercentile, 0);
      previousPercentile = percentile;
      const barHeight = Math.min(incremental / 8, 1) * 285;

      return {
        x: 70 + index * (750 / (PARTICLE_SIZE_BINS.length - 1)),
        y: 330 - (percentile / 100) * 285,
        size,
        percentile,
        incremental,
        barY: 330 - barHeight,
        barHeight,
        label: size < 10 ? size.toFixed(2) : size.toFixed(0)
      };
    });
  });
  protected readonly particlePolyline = computed(() =>
    this.particleChartPoints().map(point => `${point.x},${point.y}`).join(' ')
  );

  @Input() set mudCheckDataInput(data: ServiceCompanyMudCheckPropertiesDto | null) {
    this.mudCheckData.set(data);
  }

  @Input() set reportDataInput(data: JobRequestReportDto | null) {
    this.reportData.set(data);
  }

  protected customParameterValue(parameter: CustomReportParameter): string {
    return this.displayReportValue(parameter.value);
  }

  protected customTableColumnKey(column: CustomReportTableColumn): string {
    return (column.key ?? '').trim() || column.name;
  }

  protected customTableKey(table: CustomReportTable): string {
    return (table.key ?? '').trim() || table.name;
  }

  protected customTableRowIndices(rowCount: number): number[] {
    return Array.from({ length: Math.max(0, rowCount) }, (_, index) => index);
  }

  protected customTableCellValue(
    report: CustomReport,
    table: CustomReportTable,
    rowIndex: number,
    column: CustomReportTableColumn
  ): string {
    const key = `${this.customTableKey(table)}__r${rowIndex + 1}__${this.customTableColumnKey(column)}`;
    return this.displayReportValue(report.values[key]);
  }

  protected customSpreadsheetCellValue(cell: CustomReportSpreadsheetCell): string {
    return this.displayReportValue(cell.value);
  }

  protected customSpreadsheetCellLabel(cell: CustomReportSpreadsheetCell): string {
    return cell.definition?.type === 'text' ? String(cell.definition.value ?? '') : '';
  }

  protected displayReportValue(value: string | null | undefined): string {
    return value === null || value === undefined || value === '' ? '—' : value;
  }

  protected solidsWeight(percent: number | null | undefined, specificGravity: number | null | undefined): number | null {
    return percent !== null && percent !== undefined && specificGravity
      ? percent * specificGravity * 3.5
      : null;
  }

  private createCustomTestReports(report: JobRequestReportDto | null): CustomReport[] {
    if (!report) return [];

    const customReports: CustomReport[] = [];
    for (const group of report.overviewGroups ?? []) {
      for (const overview of group.overviews ?? []) {
        for (const row of overview.rows ?? []) {
          if (!row.customTestDefinitionJson) continue;

          let definition: CustomReportDefinition;
          try {
            definition = JSON.parse(row.customTestDefinitionJson) as CustomReportDefinition;
          } catch {
            continue;
          }

          const values = this.mergeCustomReportValues(row);
          const inputParameters = (definition.inputParameters ?? []).map(parameter => {
            const key = this.customParameterKey(parameter.name, parameter.key);
            return {
              key,
              name: parameter.name,
              unit: parameter.unit,
              value: this.lookupCustomReportValue(row.inputParameters, values, key)
            };
          });
          const outputParameters = (definition.outputParameters ?? []).map(parameter => {
            const key = this.customParameterKey(parameter.name, parameter.key);
            return {
              key,
              name: parameter.name,
              unit: parameter.unit,
              formula: parameter.formula,
              value: this.lookupCustomReportValue(row.outputParameters, values, key)
            };
          });

          customReports.push({
            label: row.label,
            testName: definition.testName?.trim() || row.label,
            inputParameters,
            outputParameters,
            table: definition.table ?? null,
            spreadsheet: definition.spreadsheet ?? null,
            spreadsheetCells: this.createCustomSpreadsheetCells(definition.spreadsheet, values),
            values
          });
        }
      }
    }

    return customReports;
  }

  private mergeCustomReportValues(row: {
    inputParameters?: Record<string, string | null>;
    outputParameters?: Record<string, string | null>;
    parameters?: Record<string, string | null>;
  }): Record<string, string | null> {
    return {
      ...(row.parameters ?? {}),
      ...(row.inputParameters ?? {}),
      ...(row.outputParameters ?? {})
    };
  }

  private lookupCustomReportValue(
    preferred: Record<string, string | null> | undefined,
    values: Record<string, string | null>,
    key: string
  ): string | null {
    if (preferred && Object.prototype.hasOwnProperty.call(preferred, key)) {
      return preferred[key] ?? null;
    }

    const matchingKey = Object.keys(values).find(existingKey => existingKey.toLowerCase() === key.toLowerCase());
    return matchingKey ? values[matchingKey] ?? null : null;
  }

  private customParameterKey(name: string, key?: string | null): string {
    return (key ?? '').trim() || name;
  }

  private createCustomSpreadsheetCells(
    worksheet: Worksheet | null | undefined,
    values: Record<string, string | null>
  ): CustomReportSpreadsheetCell[] {
    if (!worksheet) return [];

    const cells: CustomReportSpreadsheetCell[] = [];
    for (let row = 0; row < worksheet.rows; row++) {
      for (let column = 0; column < worksheet.columns; column++) {
        const address = this.spreadsheetAddress(row, column);
        const definition = worksheet.cells[address] ?? null;
        const value = definition?.type === 'text'
          ? String(definition.value ?? '')
          : this.lookupCustomReportValue(undefined, values, address);

        cells.push({ address, row, column, definition, value });
      }
    }

    return cells;
  }

  private spreadsheetAddress(row: number, column: number): string {
    let columnName = '';
    let remaining = column + 1;
    while (remaining > 0) {
      const remainder = (remaining - 1) % 26;
      columnName = String.fromCharCode(65 + remainder) + columnName;
      remaining = Math.floor((remaining - 1) / 26);
    }

    return `${columnName}${row + 1}`;
  }

  private calculateNafChemistryReportValues(
    report: JobRequestReportDto | null,
    fallback: ServiceCompanyMudCheckPropertiesDto | null
  ): NafChemistryReportValues {
    const values = this.createOverviewValueMap(report);
    const nafValues = this.createNafChemistryValueMap(report);
    const retortValues = this.createTestValueMap(report, ['retort']);
    const mudWeightValues = this.createTestValueMap(report, ['density', 'pycnometer']);
    const mudUsedVolume = this.firstFinitePositive(
      this.numberValue(nafValues, 'mudUsedVolume'),
      this.numberValue(nafValues, 'wholeMudSampleVolume'),
      this.numberValue(nafValues, 'mudSampleVolume')
    ) ?? 0;
    const alkalineDemand = this.numberValue(nafValues, 'alkalineDemand') ?? 0;
    const acidVolume = this.firstFinitePositive(
      this.numberValue(nafValues, 'acidVolume'),
      this.numberValue(nafValues, 'wholeMudAcidVolume')
    ) ?? 0;
    const silverNitrateVolume = this.firstFinitePositive(
      this.numberValue(nafValues, 'agNo3Volume'),
      this.numberValue(nafValues, 'wholeMudSilverNitrateVolume')
    ) ?? 0;
    const edtaVolume = this.firstFinitePositive(
      this.numberValue(nafValues, 'edtaVolume'),
      this.numberValue(nafValues, 'wholeMudEdtaVolume')
    ) ?? 0;
    const silverNitrateConcentration = this.firstFinitePositive(
      this.numberValue(nafValues, 'wholeMudSilverNitrateConcentration'),
      this.numberValue(nafValues, 'agNo3Concentration'),
      this.numberValue(nafValues, 'silverNitrateConcentration'),
      10000
    ) ?? 10000;
    const edtaConcentration = this.firstFinitePositive(
      this.numberValue(nafValues, 'edtaConcentration'),
      this.numberValue(nafValues, 'wholeMudEdtaConcentration'),
      4000
    ) ?? 4000;
    const titration = calculateNafChemistryTitration({
      mudUsedVolume,
      alkalineDemand,
      acidVolume,
      silverNitrateVolume,
      edtaVolume,
      silverNitrateConcentration,
      edtaConcentration
    });

    const reportDensityPpg = this.numberValue(mudWeightValues, 'density, lb/gal', 'densityppg')
      ?? this.numberValue(values, 'density, lb/gal', 'densityppg')
      ?? fallback?.density;
    const mudSpecificGravity = this.firstFinitePositive(
      this.numberValue(mudWeightValues, 'measuredSpecificGravity'),
      this.numberValue(mudWeightValues, 'specificGravity'),
      this.numberValue(nafValues, 'measuredSpecificGravity'),
      this.numberValue(nafValues, 'specificGravity'),
      reportDensityPpg === undefined ? null : reportDensityPpg / 8.345
    );
    const cellMudWeight = this.numberValue(retortValues, 'cellMudWeight');
    const cellTareWeight = this.numberValue(retortValues, 'cellTareWeight');
    const waterVolume = this.numberValue(retortValues, 'waterVolume');
    const totalVolume = this.numberValue(retortValues, 'totalVolume');
    const retortMudVolume = mudSpecificGravity !== null
      && cellMudWeight !== undefined
      && cellTareWeight !== undefined
      ? (cellMudWeight - cellTareWeight) / mudSpecificGravity
      : null;
    const waterVolumePercent = retortMudVolume !== null && retortMudVolume > 0 && waterVolume !== undefined
      ? (100 * waterVolume) / retortMudVolume
      : null;
    const nafVolumePercent = retortMudVolume !== null
      && retortMudVolume > 0
      && waterVolume !== undefined
      && totalVolume !== undefined
      ? (100 * Math.max(totalVolume - waterVolume, 0)) / retortMudVolume
      : null;
    const hasSilverNitrateVolume = this.numberValue(nafValues, 'agNo3Volume') !== undefined
      || this.numberValue(nafValues, 'wholeMudSilverNitrateVolume') !== undefined;
    const hasEdtaVolume = this.numberValue(nafValues, 'edtaVolume') !== undefined
      || this.numberValue(nafValues, 'wholeMudEdtaVolume') !== undefined;
    const silverNitrateToMudRatio = titration?.silverNitrateToMudRatio ?? null;
    const edtaToMudRatio = titration?.edtaToMudRatio ?? null;
    const waterPhaseSalinity = hasSilverNitrateVolume && hasEdtaVolume
      ? calculateNafWaterPhaseSalinity(silverNitrateToMudRatio, edtaToMudRatio, waterVolumePercent)
      : null;
    const correctedSolidsVolumePercent = hasSilverNitrateVolume && hasEdtaVolume
      ? calculateNafCorrectedSolidsVolumePercent(
          silverNitrateToMudRatio,
          edtaToMudRatio,
          nafVolumePercent,
          waterVolumePercent
        )
      : null;
    const solidsComposition = hasSilverNitrateVolume && hasEdtaVolume
      ? calculateNafSolidsComposition(
          silverNitrateToMudRatio,
          edtaToMudRatio,
          nafVolumePercent,
          waterVolumePercent,
          mudSpecificGravity,
          this.firstFinitePositive(this.numberValue(values, 'Base Fluid SG'), report?.baseFluidSpecificGravity),
          this.firstFinitePositive(this.numberValue(values, 'Low Gravity Solids SG'), report?.lowGravitySolidsSpecificGravity),
          this.firstFinitePositive(this.numberValue(values, 'Barite SG'), report?.bariteSpecificGravity)
        )
      : null;

    return {
      alkalinity: this.numberValue(nafValues, 'salinity_nafMudAlkalinity')
        ?? titration?.nafMudAlkalinity
        ?? fallback?.alkalinity,
      chlorides: this.numberValue(nafValues, 'salinity_wholeMudChlorides')
        ?? titration?.wholeMudChlorides
        ?? fallback?.chlorides,
      limeContent: this.numberValue(nafValues, 'salinity_limeContent')
        ?? titration?.limeContent
        ?? fallback?.excessLime,
      calcium: this.numberValue(nafValues, 'salinity_wholeMudCalcium')
        ?? titration?.wholeMudCalcium
        ?? fallback?.calcium,
      waterPhaseSalinity: this.numberValue(nafValues, 'salinity_waterPhaseSalinity')
        ?? waterPhaseSalinity
        ?? fallback?.waterPhaseSalinity,
      correctedSolids: this.numberValue(nafValues, 'salinity_correctedSolidsVolumePercent')
        ?? correctedSolidsVolumePercent
        ?? fallback?.corrSolids,
      lowGravitySolids: this.numberValue(nafValues, 'salinity_lowGravitySolidsVolumePercent')
        ?? solidsComposition?.lowGravitySolidsVolumePercent
        ?? fallback?.lgsPercent
    };
  }

  private createMudCheckDataFromOverview(report: JobRequestReportDto | null): ServiceCompanyMudCheckPropertiesDto | null {
    if (!report) return null;

    const values = this.createOverviewValueMap(report);
    if (values.size === 0) return null;

    const firstRheologyTemperature = [...values.keys()]
      .map(key => /^t(\d+)_rpm600$/.exec(key)?.[1])
      .find((temperature): temperature is string => !!temperature);
    const rheologyKey = (suffix: string) => firstRheologyTemperature
      ? `t${firstRheologyTemperature}_${suffix}`
      : suffix;

    return {
      id: 0,
      density: this.numberValue(values, 'density, lb/gal', 'densityppg'),
      rheologyTemperature: this.toNumber(firstRheologyTemperature),
      rpm600: this.numberValue(values, rheologyKey('rpm600'), 'rpm600'),
      rpm300: this.numberValue(values, rheologyKey('rpm300'), 'rpm300'),
      rpm200: this.numberValue(values, rheologyKey('rpm200'), 'rpm200'),
      rpm100: this.numberValue(values, rheologyKey('rpm100'), 'rpm100'),
      rpm6: this.numberValue(values, rheologyKey('rpm6'), 'rpm6'),
      rpm3: this.numberValue(values, rheologyKey('rpm3'), 'rpm3'),
      gel10Sec: this.numberValue(values, rheologyKey('gel10s'), 'gel10sec'),
      gel10Min: this.numberValue(values, rheologyKey('gel10m'), 'gel10min'),
      plasticViscosity: this.numberValue(values, 'plasticviscosity', 'pv'),
      yieldPoint: this.numberValue(values, 'yieldpoint', 'yp'),
      elecStability: this.numberValue(values, 'voltage1', 'esreading1voltage1', 'elecstability'),
      densityTemperature: this.numberValue(values, 'requestedtemperature', 'esreading1temperature'),
      hthp: this.numberValue(values, 'filtrate30min', 'filtratevolume', 'hthp'),
      htpTemperature: this.numberValue(values, 'testtemperature', 'temperature'),
      cakeHTHP: this.numberValue(values, 'cakethickness', 'cakehthp'),
      napWaterRatio: this.numberValue(values, 'nafwaterratio', 'napwaterratio'),
      chlorides: this.numberValue(values, 'chlorides', 'wholemudchlorides'),
      calcium: this.numberValue(values, 'calcium', 'wholemudcalcium'),
      cacl2: this.numberValue(values, 'cacl2', 'calciumchloride'),
      waterPhaseSalinity: this.numberValue(values, 'waterphasesalinity'),
      naClSoluble: this.numberValue(values, 'naclsoluble', 'totalsodiumchloride'),
      naClInsoluble: this.numberValue(values, 'naclinsoluble', 'insolublesodiumchloride'),
      excessLime: this.numberValue(values, 'excesslime', 'limecontent'),
      lgsPercent: this.numberValue(values, 'lgspercent', 'lowgravitysolids'),
      hgsPercent: this.numberValue(values, 'hgspercent', 'highgravitysolids'),
      asg: this.numberValue(values, 'asg', 'averagespecificgravityofsolids'),
      alkalinity: this.numberValue(values, 'alkalinity', 'alkalinedemand'),
      corrSolids: this.numberValue(values, 'corrsolids', 'correctedsolids'),
      checkTimestamp: new Date(report.reportDate)
    };
  }

  private createOverviewValueMap(report: JobRequestReportDto | null): Map<string, string> {
    const values = new Map<string, string>();
    if (!report) return values;

    for (const group of report.overviewGroups ?? []) {
      for (const overview of group.overviews ?? []) {
        if (!overview.isApplicable) continue;

        for (const row of overview.rows ?? []) {
          this.addValue(values, row.label, row.value);
          this.addValues(values, row.inputParameters);
          this.addValues(values, row.parameters);
          this.addValues(values, row.outputParameters, true);
        }
      }
    }
    return values;
  }

  private createNafChemistryValueMap(report: JobRequestReportDto | null): Map<string, string> {
    return this.createTestValueMap(report, ['salinity', 'nafchemistry']);
  }

  private createTestValueMap(report: JobRequestReportDto | null, testAliases: string[]): Map<string, string> {
    const values = new Map<string, string>();
    if (!report) return values;
    const acceptedAliases = new Set(testAliases);

    for (const group of report.overviewGroups ?? []) {
      for (const overview of group.overviews ?? []) {
        if (!overview.isApplicable || overview.type.trim().toLowerCase() !== 'tests') continue;

        for (const row of overview.rows ?? []) {
          const aliases = [row.testTypeAlias, row.label]
            .filter((alias): alias is string => !!alias)
            .map(alias => alias.replace(/[^a-z0-9]/gi, '').toLowerCase());
          if (!aliases.some(alias => acceptedAliases.has(alias))) continue;

          this.addValue(values, row.label, row.value);
          this.addValues(values, row.inputParameters);
          this.addValues(values, row.parameters);
          this.addValues(values, row.outputParameters, true);
          return values;
        }
      }
    }

    return values;
  }

  private addValues(
    target: Map<string, string>,
    source?: Record<string, string | null>,
    overwrite = false
  ): void {
    for (const [key, value] of Object.entries(source ?? {})) {
      this.addValue(target, key, value, overwrite);
    }
  }

  private addValue(target: Map<string, string>, key: string, value: string | null | undefined, overwrite = false): void {
    if (value === null || value === undefined || value.trim() === '') return;
    const normalizedKey = key.replace(/[_\s/-]+/g, '').toLowerCase();
    if (overwrite || !target.has(normalizedKey)) target.set(normalizedKey, value);
    if (overwrite || !target.has(key.toLowerCase())) target.set(key.toLowerCase(), value);
  }

  private numberValue(values: Map<string, string>, ...keys: string[]): number | undefined {
    for (const key of keys) {
      const value = values.get(key.replace(/[_\s/-]+/g, '').toLowerCase()) ?? values.get(key.toLowerCase());
      const parsed = this.toNumber(value);
      if (parsed !== undefined) return parsed;
    }
    return undefined;
  }

  private firstFinitePositive(...values: Array<number | null | undefined>): number | null {
    return values.find((candidate): candidate is number =>
      candidate !== null && candidate !== undefined && Number.isFinite(candidate) && candidate > 0
    ) ?? null;
  }

  private toNumber(value: string | number | undefined): number | undefined {
    if (value === undefined || value === '') return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}
